import React, { useCallback, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import clsx from 'clsx'

import { ActionFlag } from '@makyoapp/makyo-common'
import { Block16, Clipboard16, ClipboardCheck16 } from '@makyoapp/makyo-icon/icon'
import { MoreDropdown, Sidebar } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { useAppOverlaysContext } from '@/context/AppOverlaysContext'
import { useAppPagesContext } from '@/context/AppPagesContext'
import { useVariableTextSingleCell } from '@/hooks/__project__/useVariableTextSingleCell'
import { LeadingSidebarTab } from '@/shared/builder-ui'
import { formatFormDetailScreenTitle } from '@/utils/builder/app-builder'

import { SortableTree } from './components/SortableTree'
import TreeControl from './components/TreeControl'
import { SortableTreeProvider, useSortableTreeContext } from './contexts/SortableTreeContext'

const NavigatorLayout = React.memo(() => {
  const { clearClipboard, clipboard, clipboardAll, formClipboard, handleCopyAll, handlePasteAll, handlePaste } =
    useSortableTreeContext()
  const hasClipboard = clipboard || clipboardAll || formClipboard

  const totalClipboardCount = useMemo(() => {
    let clipboardCount = clipboard ? 1 : 0
    let clipboardAllCount = clipboardAll ? clipboardAll.elements.length : 0
    let formClipboardCount = formClipboard ? 1 : 0

    return clipboardCount + clipboardAllCount + formClipboardCount
    //
  }, [clipboard, clipboardAll, formClipboard])

  const { setPageAction, setActiveTab } = useAppBuilderContext()
  const { activeLayerContainer, activeLayer } = useActiveLayerContext()
  const { setOverlayAction } = useAppOverlaysContext()
  const { activePage } = useAppPagesContext()

  const activeLayerTitle = useVariableTextSingleCell(activeLayerContainer?.title)
  const activePageTitle = useVariableTextSingleCell(activePage?.title)

  const title = useMemo(() => {
    if (activeLayer.tableId) {
      const isFormAddScreen = activeLayerTitle.startsWith('form-add')
      const isFormDetailScreen = activeLayerTitle.startsWith('form-detail')
      if (isFormAddScreen || isFormDetailScreen) {
        return formatFormDetailScreenTitle(activeLayerTitle)
      }
      return 'Details'
    }

    if (activeLayer.type === 'overlay') {
      return `${activePageTitle} / ${activeLayerTitle}`
    }

    return activeLayerTitle
    //
  }, [activeLayer, activeLayerTitle, activePageTitle])

  const handleTitleClick = useCallback(() => {
    if (activeLayer.tableId) {
      setPageAction({
        flag: ActionFlag.Edit,
        page: activeLayer.id,
      })
      setActiveTab(LeadingSidebarTab.Page)
    } else {
      if (activeLayer.type === 'page') {
        setPageAction({
          page: activeLayer.id,
          flag: ActionFlag.Edit,
        })
        setActiveTab(LeadingSidebarTab.Page)
      } else {
        setPageAction(undefined)
        setOverlayAction({
          page: activeLayer.id,
          flag: ActionFlag.Edit,
        })
        setActiveTab(LeadingSidebarTab.Page)
      }
    }
  }, [activeLayer, setPageAction, setActiveTab, setOverlayAction])

  const pasteElement = async () => {
    if (clipboard) {
      await handlePaste()
    }

    if (clipboardAll) {
      await handlePasteAll()
    }
  }

  return (
    <div className='navigator flex flex-col gap-3 h-full px-1'>
      {/* Breadcrumb */}
      <div className='px-2 flex-c pt-4 flex-wrap'>
        <div className={clsx('txt link text-primary-500 cursor-pointer max-w-80')} onClick={handleTitleClick}>
          {title}
        </div>
      </div>

      <div className='h-[calc(100%-76px-8px-16px)]'>
        {/* Title & Controls */}
        <div className='h-10 flex-c px-2 text-m-sm justify-between'>
          <span className='uppercase'>
            <Trans>Elements</Trans>
          </span>

          <div className='flex'>
            {/* Tree control to handle collapse and expand of the tree */}
            <TreeControl />

            <MoreDropdown menuClassName='z-10 text-m-m'>
              <MoreDropdown.MenuItem
                onClick={() => {
                  clearClipboard()
                }}
                className={clsx(!hasClipboard && 'cursor-not-allowed opacity-50')}
              >
                <Block16 />
                <span>
                  <Trans>Clear Clipboard ({{ totalClipboardCount }})</Trans>
                </span>
              </MoreDropdown.MenuItem>

              <MoreDropdown.MenuItem
                onClick={pasteElement}
                className={clsx(!clipboard && 'cursor-not-allowed opacity-50')}
              >
                <ClipboardCheck16 />
                <span>
                  <Trans>Paste Element</Trans>
                </span>
              </MoreDropdown.MenuItem>

              <MoreDropdown.MenuItem onClick={handleCopyAll}>
                <Clipboard16 />
                <span>
                  <Trans>Copy All</Trans>
                </span>
              </MoreDropdown.MenuItem>

              <MoreDropdown.MenuItem
                onClick={pasteElement}
                className={clsx(!clipboardAll && 'cursor-not-allowed opacity-50')}
              >
                <ClipboardCheck16 />
                <span>
                  <Trans>Paste All</Trans>
                </span>
              </MoreDropdown.MenuItem>
            </MoreDropdown>
          </div>
        </div>

        {/* Components Tree Navigation */}
        <SortableTree />
      </div>
    </div>
  )
})
NavigatorLayout.displayName = 'NavigatorLayout'

/**
 * AppBuilder Navigator
 */
export default function Navigator() {
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const savedWidth = localStorage.getItem('sidebarWidth')
    return savedWidth ? parseInt(savedWidth, 10) : 372
  })

  const handleResizeStop = (e: any, direction: any, ref: HTMLElement, d: any) => {
    const newWidth = ref.offsetWidth
    setSidebarWidth(newWidth)
    localStorage.setItem('sidebarWidth', newWidth.toString())
  }

  return (
    <Sidebar
      onResizeStop={handleResizeStop}
      flip
      withHandle={false}
      className='h-full!'
      initialWidth={sidebarWidth}
      resizable
      maxWidth={780}
      minWidth={136}
    >
      <div className='trailing-sidebar h-full w-full bg-white dark:bg-slate-800'>
        <SortableTreeProvider>
          <NavigatorLayout />
        </SortableTreeProvider>
      </div>
    </Sidebar>
  )
}
